import { type ComponentProps } from 'react';

import { InputGroup, InputGroupAffix, InputGroupInput } from '@/components/Input/InputGroup';

type MoneyInputProps = Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type'> & {
    /** Whole pounds, or null for an empty field. */
    value: number | null;
    onChange: (value: number | null) => void;
    /** The unit in the box; £ unless a screen says otherwise. */
    currency?: string;
};

/**
 * ui-input with `prefix="£"`, the design's money field: the unit sits inside the box and the
 * amount reads the way the register prints it, with a separator every three digits, as you type.
 * The value the caller gets back is a number; the grouping is only ever what is shown.
 *
 * <p>
 * Whole units only, because the one field that uses it (a credit limit) is a bigint in the
 * legacy schema. Anything that is not a digit is dropped rather than refused, so a pasted
 * "£50,000" lands as 50000.
 * </p>
 */
function MoneyInput({ value, onChange, currency = '£', className, ...props }: MoneyInputProps) {
    return (
        <InputGroup className={className}>
            <InputGroupAffix>{currency}</InputGroupAffix>
            <InputGroupInput
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={value === null ? '' : value.toLocaleString('en-GB')}
                onChange={(event) => onChange(parseMoney(event.target.value))}
                {...props}
            />
        </InputGroup>
    );
}

/** The digits of whatever was typed, as a number; null when there are none. */
function parseMoney(text: string): number | null {
    const digits = text.replace(/\D/g, '');
    if (digits === '') return null;
    // Number keeps the bigint range comfortably; the legacy column is a bigint too.
    return Number(digits);
}

export { MoneyInput, type MoneyInputProps };
